import { z } from "zod";
import {
  AD_PLATFORMS,
  ALERT_SEVERITIES,
  ALERT_STATUSES,
  ALERT_TYPES,
  DATE_PRESETS,
  EXPERIMENT_METRICS,
  EXPERIMENT_STATUSES,
  EXPERIMENT_TYPES,
  RECOMMENDATION_STATUSES,
  RECOMMENDATION_TYPES,
} from "../enums";
import type {
  AdLevel,
  AdPlatform,
  AlertSeverity,
  AlertStatus,
  AlertType,
  ExperimentMetric,
  ExperimentStatus,
  ExperimentType,
  RecommendationStatus,
  RecommendationType,
  RiskClass,
} from "../enums";
import type { Paginated } from "./common";

// Media Buyer intelligence (WP6b): recommendations, alerts, experiments, decisions journal, guardrails and the AI
// media buyer. Request schemas shared by the API (validation) and the web app (forms), the DTOs the API returns, and
// the pure economics helper used by both the rules engine and the Profil business page. Money is integer minor units
// in the client currency unless a field says otherwise (budgets stay in the ad account currency).

const objectIdString = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const minor = z.number().int("Montant invalide").min(0, "Montant invalide");
const page = z.coerce.number().int().min(1).default(1);

// ---------------------------------------------------------------------------------------------------------------
// Vocabularies and French labels

export const RECOMMENDATION_SOURCES = ["rules", "ai", "growth"] as const;
export type RecommendationSource = (typeof RECOMMENDATION_SOURCES)[number];

export const RECOMMENDATION_ACTION_KINDS = ["set_budget", "set_status", "duplicate", "create_audience", "none"] as const;
export type RecommendationActionKind = (typeof RECOMMENDATION_ACTION_KINDS)[number];

export const DECISION_ACTIONS = ["set_status", "set_budget", "duplicate", "launch_campaign", "create_audience", "upload_media"] as const;
export type DecisionAction = (typeof DECISION_ACTIONS)[number];

export const IMPACT_VERDICTS = ["positive", "neutral", "negative"] as const;
export type ImpactVerdict = (typeof IMPACT_VERDICTS)[number];

export const AUTO_RULE_LEVELS = ["campaign", "adset", "ad"] as const;
export type AutoRuleLevel = (typeof AUTO_RULE_LEVELS)[number];
export const AUTO_RULE_METRICS = ["roas", "cpa", "ctr", "frequency", "spend", "cpm"] as const;
export type AutoRuleMetric = (typeof AUTO_RULE_METRICS)[number];
export const AUTO_RULE_OPERATORS = ["lt", "gt"] as const;
export type AutoRuleOperator = (typeof AUTO_RULE_OPERATORS)[number];
export const AUTO_RULE_ACTIONS = ["alert", "recommend_pause", "recommend_decrease"] as const;
export type AutoRuleAction = (typeof AUTO_RULE_ACTIONS)[number];

/** Auto rule metrics whose threshold is an amount in the client currency (minor units). */
export const MONEY_AUTO_RULE_METRICS: readonly AutoRuleMetric[] = ["cpa", "spend", "cpm"];

export const EXPERIMENT_VARIANT_LEVELS = ["campaign", "adset", "ad", "page"] as const;
export type ExperimentVariantLevel = (typeof EXPERIMENT_VARIANT_LEVELS)[number];

export const EXPERIMENT_RESULT_STATUSES = ["insufficient", "running", "winner", "no_difference"] as const;
export type ExperimentResultStatus = (typeof EXPERIMENT_RESULT_STATUSES)[number];

export const RECOMMENDATION_SOURCE_LABELS: Record<RecommendationSource, string> = {
  rules: "Règle",
  ai: "IA",
  growth: "Moteur de croissance",
};

export const DECISION_ACTION_LABELS: Record<DecisionAction, string> = {
  set_status: "Changement de statut",
  set_budget: "Budget quotidien",
  duplicate: "Duplication",
  launch_campaign: "Lancement de campagne",
  create_audience: "Création d'audience",
  upload_media: "Envoi de média",
};

export const IMPACT_VERDICT_LABELS: Record<ImpactVerdict, string> = {
  positive: "Impact positif",
  neutral: "Impact neutre",
  negative: "Impact négatif",
};

export const AD_LEVEL_LABELS: Record<AdLevel, string> = {
  account: "Compte publicitaire",
  campaign: "Campagne",
  adset: "Ensemble de publicités",
  ad: "Publicité",
};

export const AUTO_RULE_METRIC_LABELS: Record<AutoRuleMetric, string> = {
  roas: "ROAS",
  cpa: "Coût par commande (CPA)",
  ctr: "Taux de clic (CTR, %)",
  frequency: "Fréquence",
  spend: "Dépenses",
  cpm: "CPM (coût pour 1 000 impressions)",
};

export const AUTO_RULE_OPERATOR_LABELS: Record<AutoRuleOperator, string> = {
  lt: "inférieur à",
  gt: "supérieur à",
};

export const AUTO_RULE_ACTION_LABELS: Record<AutoRuleAction, string> = {
  alert: "Créer une alerte",
  recommend_pause: "Recommander la pause",
  recommend_decrease: "Recommander une baisse de budget",
};

export const EXPERIMENT_METRIC_LABELS: Record<ExperimentMetric, string> = {
  roas: "ROAS (revenu / dépense)",
  cpa: "Coût par conversion",
  ctr: "Taux de clic",
  conversion_rate: "Taux de conversion",
  revenue_per_visitor: "Revenu par visiteur",
};

export const EXPERIMENT_VARIANT_LEVEL_LABELS: Record<ExperimentVariantLevel, string> = {
  campaign: "Campagnes",
  adset: "Ensembles de publicités",
  ad: "Publicités",
  page: "Page du site",
};

export const EXPERIMENT_RESULT_STATUS_LABELS: Record<ExperimentResultStatus, string> = {
  insufficient: "Données insuffisantes",
  running: "En cours",
  winner: "Gagnant identifié",
  no_difference: "Pas de différence détectable",
};

// ---------------------------------------------------------------------------------------------------------------
// Recommendations

export const recommendationListQuery = z.object({
  status: z.enum([...RECOMMENDATION_STATUSES, "history", "all"]).default("open"),
  type: z.enum(RECOMMENDATION_TYPES).optional(),
  platform: z.enum(AD_PLATFORMS).optional(),
  priority: z.coerce.number().int().min(1).max(5).optional(),
  source: z.enum(RECOMMENDATION_SOURCES).optional(),
  page,
  pageSize: z.coerce.number().int().min(1).max(100).optional(),
  /** Alias of pageSize (overview card: ?status=open&limit=4). */
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type RecommendationListQuery = z.infer<typeof recommendationListQuery>;

export const recommendationApproveInput = z.object({
  reason: z.string().trim().max(500).optional().default(""),
  /** Approve and execute in one step (requires ads.execute). */
  execute: z.boolean().optional().default(false),
});
export type RecommendationApproveInput = z.infer<typeof recommendationApproveInput>;

export const recommendationRejectInput = z.object({
  reason: z.string().trim().min(3, "Indiquez le motif du refus").max(500, "500 caractères maximum"),
});
export type RecommendationRejectInput = z.infer<typeof recommendationRejectInput>;

export const recommendationDismissInput = z.object({
  reason: z.string().trim().max(500, "500 caractères maximum").optional().default(""),
});
export type RecommendationDismissInput = z.infer<typeof recommendationDismissInput>;

export const recommendationBulkApproveInput = z.object({
  ids: z.array(objectIdString).min(1, "Sélectionnez au moins une recommandation").max(50, "50 recommandations au maximum"),
  reason: z.string().trim().max(500).optional().default(""),
});
export type RecommendationBulkApproveInput = z.infer<typeof recommendationBulkApproveInput>;

// ---------------------------------------------------------------------------------------------------------------
// Alerts

export const alertListQuery = z.object({
  /** active = open and acknowledged. */
  status: z.enum([...ALERT_STATUSES, "active", "all"]).default("active"),
  severity: z.enum(ALERT_SEVERITIES).optional(),
  type: z.enum(ALERT_TYPES).optional(),
  platform: z.enum(AD_PLATFORMS).optional(),
  page,
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
});
export type AlertListQuery = z.infer<typeof alertListQuery>;

export const alertActionInput = z.object({
  note: z.string().trim().max(300, "300 caractères maximum").optional().default(""),
});
export type AlertActionInput = z.infer<typeof alertActionInput>;

// ---------------------------------------------------------------------------------------------------------------
// Experiments

export const experimentVariantInput = z.object({
  key: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_]{1,12}$/, "Clé invalide")
    .optional(),
  name: z.string().trim().min(1, "Nom de variante requis").max(80, "80 caractères maximum"),
  entityLevel: z.enum(EXPERIMENT_VARIANT_LEVELS).default("adset"),
  entityExternalIds: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  pageId: objectIdString.nullable().default(null),
  description: z.string().trim().max(300).default(""),
});
export type ExperimentVariantInput = z.infer<typeof experimentVariantInput>;

const experimentBase = z.object({
  name: z.string().trim().min(3, "Nom trop court").max(120, "120 caractères maximum"),
  hypothesis: z.string().trim().max(1000, "1 000 caractères maximum").default(""),
  type: z.enum(EXPERIMENT_TYPES),
  platform: z.enum([...AD_PLATFORMS, "site"]).default("meta"),
  metric: z.enum(EXPERIMENT_METRICS).default("cpa"),
  variants: z.array(experimentVariantInput).min(2, "Ajoutez au moins deux variantes").max(5, "Cinq variantes au maximum"),
  minSamplePerVariant: z.number().int().min(10, "10 conversions minimum").max(100_000).default(100),
  endAt: z.string().datetime({ offset: true }).nullable().default(null),
});

function checkVariants(v: { platform?: string; variants?: ExperimentVariantInput[] }, ctx: z.RefinementCtx): void {
  if (!v.variants) return;
  const keys = new Set<string>();
  v.variants.forEach((variant, i) => {
    if (variant.key) {
      if (keys.has(variant.key)) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["variants", i, "key"], message: "Clé déjà utilisée" });
      keys.add(variant.key);
    }
    if (variant.entityLevel === "page") {
      if (!variant.pageId) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["variants", i, "pageId"], message: "Choisissez une page" });
    } else if (variant.entityExternalIds.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["variants", i, "entityExternalIds"], message: "Choisissez au moins un élément" });
    }
    if (v.platform === "site" && variant.entityLevel !== "page") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["variants", i, "entityLevel"], message: "Un test de site compare des pages" });
    }
    if (v.platform && v.platform !== "site" && variant.entityLevel === "page") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["variants", i, "entityLevel"], message: "Choisissez la plateforme Site pour comparer des pages" });
    }
  });
  const levels = new Set(v.variants.map((x) => x.entityLevel));
  if (levels.size > 1) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["variants"], message: "Toutes les variantes doivent comparer le même niveau" });
}

export const experimentInput = experimentBase.superRefine(checkVariants);
export type ExperimentInput = z.infer<typeof experimentInput>;
export type ExperimentFormInput = z.input<typeof experimentInput>;

export const experimentUpdateInput = experimentBase.partial().superRefine(checkVariants);
export type ExperimentUpdateInput = z.infer<typeof experimentUpdateInput>;

export const experimentListQuery = z.object({
  status: z.enum([...EXPERIMENT_STATUSES, "all"]).default("all"),
  page,
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type ExperimentListQuery = z.infer<typeof experimentListQuery>;

export const experimentStopInput = z.object({ reason: z.string().trim().max(300, "300 caractères maximum").optional().default("") });
export type ExperimentStopInput = z.infer<typeof experimentStopInput>;

export const experimentWinnerInput = z.object({
  variantKey: z.string().trim().min(1, "Choisissez la variante gagnante").max(12),
  note: z.string().trim().max(300, "300 caractères maximum").optional().default(""),
});
export type ExperimentWinnerInput = z.infer<typeof experimentWinnerInput>;

// ---------------------------------------------------------------------------------------------------------------
// Decisions journal

export const decisionListQuery = z.object({
  preset: z.enum(DATE_PRESETS).default("30d"),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  platform: z.enum(AD_PLATFORMS).optional(),
  action: z.enum(DECISION_ACTIONS).optional(),
  actor: z.enum(["user", "ai", "system"]).optional(),
  outcome: z.enum(["success", "failed"]).optional(),
  verdict: z.enum(IMPACT_VERDICTS).optional(),
  q: z.string().trim().max(120).optional(),
  page,
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
});
export type DecisionListQuery = z.infer<typeof decisionListQuery>;

// ---------------------------------------------------------------------------------------------------------------
// Guardrails

export const autoRuleInput = z.object({
  id: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{4,40}$/, "Identifiant invalide")
    .optional(),
  name: z.string().trim().min(2, "Nom trop court").max(80, "80 caractères maximum"),
  level: z.enum(AUTO_RULE_LEVELS).default("adset"),
  metric: z.enum(AUTO_RULE_METRICS),
  operator: z.enum(AUTO_RULE_OPERATORS),
  threshold: z.number({ invalid_type_error: "Seuil invalide" }).min(0, "Seuil invalide").max(1_000_000_000_000, "Seuil invalide"),
  windowDays: z.number().int().min(1, "1 jour minimum").max(30, "30 jours maximum").default(3),
  minSpendMinor: minor.default(0),
  action: z.enum(AUTO_RULE_ACTIONS).default("alert"),
  enabled: z.boolean().default(true),
});
export type AutoRuleInput = z.infer<typeof autoRuleInput>;

export const guardrailUpdateInput = z
  .object({
    maxDailySpendMinor: minor.nullable(),
    maxBudgetIncreasePct: z.number().int("Pourcentage entier").min(1, "1 % minimum").max(100, "100 % maximum"),
    minHoursBetweenBudgetChanges: z.number().int("Nombre d'heures entier").min(0, "Valeur invalide").max(720, "720 h maximum"),
    maxSingleBudgetMinor: minor.nullable(),
    minRoas: z.number().min(0, "ROAS invalide").max(100, "ROAS invalide").nullable(),
    maxCpaMinor: minor.nullable(),
    stopLoss: z
      .object({
        enabled: z.boolean(),
        targetCpaMultiple: z.number().min(1, "1 minimum").max(10, "10 maximum"),
        pauseCpaMultiple: z.number().min(1, "1 minimum").max(20, "20 maximum"),
        minSpendMinor: minor,
      })
      .partial(),
    creativeFatigue: z
      .object({
        enabled: z.boolean(),
        maxFrequency: z.number().min(1, "1 minimum").max(20, "20 maximum"),
        ctrDropPct: z.number().int("Pourcentage entier").min(5, "5 % minimum").max(90, "90 % maximum"),
        minImpressions: z.number().int().min(500, "500 impressions minimum").max(1_000_000),
      })
      .partial(),
    autoRules: z.array(autoRuleInput).max(20, "20 règles maximum"),
    requireApproval: z
      .object({ budgetIncrease: z.boolean(), budgetDecrease: z.boolean(), activate: z.boolean(), pause: z.boolean(), createCampaign: z.boolean() })
      .partial(),
    aiProposalsEnabled: z.boolean(),
    killSwitch: z.boolean(),
    allowedPlatforms: z.array(z.enum(AD_PLATFORMS)).max(AD_PLATFORMS.length),
  })
  .partial()
  .superRefine((v, ctx) => {
    const warn = v.stopLoss?.targetCpaMultiple;
    const pause = v.stopLoss?.pauseCpaMultiple;
    if (typeof warn === "number" && typeof pause === "number" && pause < warn) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["stopLoss", "pauseCpaMultiple"], message: "Le seuil de pause doit être supérieur ou égal au seuil d'alerte" });
    }
  });
export type GuardrailUpdateInput = z.infer<typeof guardrailUpdateInput>;

// ---------------------------------------------------------------------------------------------------------------
// AI media buyer

export const mediaBuyerChatInput = z.object({
  message: z.string().trim().min(1, "Écrivez un message").max(4000, "4 000 caractères maximum"),
  threadId: objectIdString.nullable().optional(),
});
export type MediaBuyerChatInput = z.infer<typeof mediaBuyerChatInput>;

export const mediaBuyerBriefingInput = z.object({ refresh: z.boolean().optional().default(false) });

// ---------------------------------------------------------------------------------------------------------------
// COD-true economics (docs/research/GROWTH-ALGORITHMS.md section 9). Pure: used by the API rules and the web page.

export const AD_TARGET_PROFIT_SHARE = 0.2;
/** Market prior for the delivery rate when the merchant has not entered one (decision C6, labelled as an estimate). */
export const DEFAULT_DELIVERY_RATE_PCT = 75;

export interface AdEconomicsInput {
  averageOrderValueMinor?: number | null;
  grossMarginPct?: number | null;
  expectedDeliveryRatePct?: number | null;
  avgShippingCostMinor?: number | null;
  rtoCostMinor?: number | null;
  targetRoas?: number | null;
  targetCpaMinor?: number | null;
}

export type AdEconomicsField = "averageOrderValueMinor" | "grossMarginPct" | "expectedDeliveryRatePct" | "avgShippingCostMinor" | "rtoCostMinor";

export interface AdTargets {
  /** Contribution of a delivered order (order value x margin minus the shipping cost). */
  contributionDeliveredMinor: number | null;
  /** Loss of a refused or returned order (RTO). */
  lossRtoMinor: number | null;
  deliveryRatePct: number | null;
  /** Maximum ad cost per confirmed order before losing money. */
  breakEvenCpaMinor: number | null;
  /** Break-even ROAS on the value of confirmed orders. */
  breakEvenRoas: number | null;
  /** Break-even ROAS on delivered (cashed) revenue. */
  breakEvenDeliveredRoas: number | null;
  /** Suggested targets keeping AD_TARGET_PROFIT_SHARE of the contribution as profit. */
  suggestedCpaMinor: number | null;
  suggestedRoas: number | null;
  /** Targets used by the rules: the merchant's own values first, then the suggested ones. */
  targetCpaMinor: number | null;
  targetRoas: number | null;
  targetSource: "explicit" | "computed" | "missing";
  /** Unprofitable before ads: every delivered order already loses money. */
  unprofitable: boolean;
  missing: AdEconomicsField[];
  assumptions: string[];
}

/** Break-even and target CPA / ROAS from the business profile economics (all amounts in the same currency). */
export function adTargetsFromEconomics(e: AdEconomicsInput | null | undefined, profitShare = AD_TARGET_PROFIT_SHARE): AdTargets {
  const econ: AdEconomicsInput = e ?? {};
  const value = typeof econ.averageOrderValueMinor === "number" && econ.averageOrderValueMinor > 0 ? econ.averageOrderValueMinor : null;
  const margin = typeof econ.grossMarginPct === "number" ? econ.grossMarginPct : null;
  const shipping = typeof econ.avgShippingCostMinor === "number" ? econ.avgShippingCostMinor : null;
  const rtoCost = typeof econ.rtoCostMinor === "number" ? econ.rtoCostMinor : null;
  const delivery = typeof econ.expectedDeliveryRatePct === "number" ? econ.expectedDeliveryRatePct : null;
  const missing: AdEconomicsField[] = [];
  const assumptions: string[] = [];
  if (value === null) missing.push("averageOrderValueMinor");
  if (margin === null) missing.push("grossMarginPct");
  if (shipping === null) missing.push("avgShippingCostMinor");
  if (rtoCost === null) missing.push("rtoCostMinor");
  if (delivery === null) missing.push("expectedDeliveryRatePct");

  const explicitCpa = typeof econ.targetCpaMinor === "number" && econ.targetCpaMinor > 0 ? econ.targetCpaMinor : null;
  const explicitRoas = typeof econ.targetRoas === "number" && econ.targetRoas > 0 ? econ.targetRoas : null;

  let contribution: number | null = null;
  let loss: number | null = null;
  let breakEvenCpa: number | null = null;
  let breakEvenRoas: number | null = null;
  let breakEvenDeliveredRoas: number | null = null;
  let suggestedCpa: number | null = null;
  let suggestedRoas: number | null = null;
  let unprofitable = false;
  const deliveryPct = delivery ?? DEFAULT_DELIVERY_RATE_PCT;
  const computable = value !== null && margin !== null;

  if (value !== null && margin !== null) {
    const shippingCost = shipping ?? 0;
    if (shipping === null) assumptions.push("Coût de livraison non renseigné : compté à 0, ajoutez-le pour un calcul exact.");
    if (delivery === null) assumptions.push(`Taux de livraison non renseigné : ${DEFAULT_DELIVERY_RATE_PCT} % utilisé (moyenne du marché, estimation).`);
    loss = rtoCost ?? shippingCost;
    if (rtoCost === null) assumptions.push("Coût d'un retour non renseigné : estimé au coût de livraison aller.");
    const d = Math.min(1, Math.max(0, deliveryPct / 100));
    contribution = Math.round((value * margin) / 100) - shippingCost;
    const expected = d * contribution - (1 - d) * loss;
    if (expected <= 0) {
      unprofitable = true;
    } else {
      breakEvenCpa = Math.round(expected);
      breakEvenRoas = Math.round((value / expected) * 100) / 100;
      breakEvenDeliveredRoas = Math.round(((d * value) / expected) * 100) / 100;
      suggestedCpa = Math.round(expected * (1 - profitShare));
      suggestedRoas = suggestedCpa > 0 ? Math.round((value / suggestedCpa) * 100) / 100 : null;
    }
  }

  let targetCpa = explicitCpa ?? suggestedCpa;
  if (!explicitCpa && explicitRoas && value !== null) targetCpa = Math.round(value / explicitRoas);
  let targetRoas = explicitRoas ?? suggestedRoas;
  if (!explicitRoas && explicitCpa && value !== null) targetRoas = Math.round((value / explicitCpa) * 100) / 100;
  const targetSource: AdTargets["targetSource"] = explicitCpa || explicitRoas ? "explicit" : targetCpa ? "computed" : "missing";

  return {
    contributionDeliveredMinor: contribution,
    lossRtoMinor: loss,
    deliveryRatePct: computable || delivery !== null ? deliveryPct : null,
    breakEvenCpaMinor: breakEvenCpa,
    breakEvenRoas,
    breakEvenDeliveredRoas,
    suggestedCpaMinor: suggestedCpa,
    suggestedRoas,
    targetCpaMinor: targetCpa ?? null,
    targetRoas: targetRoas ?? null,
    targetSource,
    unprofitable,
    missing,
    assumptions,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// DTOs

export interface EvidenceItemDto {
  label: string;
  value: string;
  tone?: "good" | "bad" | "neutral";
}

export interface RecommendationEvidenceDto {
  window: string | null;
  items: EvidenceItemDto[];
  thresholds: EvidenceItemDto[];
  note: string | null;
}

export interface RecommendationDto {
  id: string;
  source: RecommendationSource;
  /** Who created it: a rule, the AI layer or a member asking for approval. */
  origin: "rule" | "ai" | "manual" | "experiment";
  type: RecommendationType;
  platform: AdPlatform | null;
  level: AdLevel | null;
  entityExternalId: string | null;
  entityName: string;
  title: string;
  rationale: string;
  aiRationale: string | null;
  evidence: RecommendationEvidenceDto;
  expectedImpact: { metric: string | null; deltaPct: number | null; text: string };
  action: { kind: RecommendationActionKind; params: Record<string, unknown> | null };
  actionSummary: string;
  /** true when execution calls an ad platform (financial and external actions). */
  executable: boolean;
  riskClass: RiskClass;
  priority: number;
  aiPriority: number | null;
  confidence: number;
  status: RecommendationStatus;
  guardrailCheck: { passed: boolean; violations: string[] };
  decidedBy: { type: string; name: string | null } | null;
  decidedAt: string | null;
  decisionReason: string;
  executedBy: { type: string; name: string | null } | null;
  executedAt: string | null;
  executionResult: unknown;
  error: string | null;
  expiresAt: string | null;
  link: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecommendationListDto {
  items: RecommendationDto[];
  total: number;
  page: number;
  pageSize: number;
  counts: { open: number; approved: number; history: number };
}

export interface RecommendationActionResultDto {
  recommendation: RecommendationDto;
  message: string;
}

export interface BulkApproveResultDto {
  approved: number;
  skipped: Array<{ id: string; reason: string }>;
}

export interface EvaluationSummaryDto {
  accounts: number;
  recommendations: { created: number; updated: number; expired: number };
  alerts: { opened: number; updated: number; resolved: number };
  skipped: string[];
  aiReviewed: number;
}

export interface AdAlertDto {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  status: AlertStatus;
  platform: AdPlatform | null;
  level: AdLevel | null;
  entityExternalId: string | null;
  entityName: string;
  title: string;
  message: string;
  metrics: EvidenceItemDto[];
  link: string | null;
  detectedAt: string;
  lastSeenAt: string | null;
  occurrences: number;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  autoResolved: boolean;
  note: string;
}

export interface AlertListDto {
  items: AdAlertDto[];
  total: number;
  page: number;
  pageSize: number;
  counts: { critical: number; warning: number; info: number; active: number };
}

export interface ExperimentVariantResultDto {
  key: string;
  name: string;
  spendMinor: number;
  impressions: number;
  clicks: number;
  trials: number;
  conversions: number;
  revenueMinor: number;
  value: number | null;
  valueText: string;
  probabilityBest: number | null;
  expectedLoss: number | null;
  liftVsControl: { mean: number; low: number; high: number } | null;
  sampleOk: boolean;
}

export interface ExperimentResultsDto {
  computedAt: string;
  metric: ExperimentMetric;
  currency: string;
  days: number;
  status: ExperimentResultStatus;
  conclusion: string;
  winnerKey: string | null;
  conversionsSource: "orders" | "platform";
  variants: ExperimentVariantResultDto[];
}

export interface ExperimentDto {
  id: string;
  name: string;
  hypothesis: string;
  type: ExperimentType;
  platform: AdPlatform | "site";
  metric: ExperimentMetric;
  status: ExperimentStatus;
  variants: Array<{ key: string; name: string; entityLevel: ExperimentVariantLevel; entityExternalIds: string[]; pageId: string | null; description: string; entityNames: string[] }>;
  startAt: string | null;
  endAt: string | null;
  minSamplePerVariant: number;
  results: ExperimentResultsDto | null;
  winnerKey: string | null;
  winnerDeclaredAt: string | null;
  recommendationId: string | null;
  stopReason: string;
  createdAt: string;
  updatedAt: string;
}

export interface ImpactReviewDto {
  horizonDays: number;
  reviewedAt: string;
  verdict: ImpactVerdict;
  summary: string;
  before: ImpactMetricsDto;
  after: ImpactMetricsDto;
}

export interface ImpactMetricsDto {
  days: number;
  spendMinor: number;
  purchases: number;
  confirmedOrders: number;
  revenueMinor: number;
  cpaMinor: number | null;
  roas: number | null;
  currency: string;
}

export interface DecisionDto {
  id: string;
  action: string;
  actionLabel: string;
  platform: AdPlatform | null;
  level: AdLevel | null;
  entityName: string;
  entityExternalId: string | null;
  outcome: "success" | "failed";
  reason: string;
  error: string | null;
  actor: { type: string; name: string | null };
  /** Origin of the change: the member alone, a rule, the AI layer, an experiment or a member request. */
  origin: "user" | "rule" | "ai" | "experiment" | "request" | "system";
  before: unknown;
  after: unknown;
  beforeText: string | null;
  afterText: string | null;
  recommendation: { id: string; title: string; type: RecommendationType; source: RecommendationSource } | null;
  experimentId: string | null;
  impactReview: ImpactReviewDto | null;
  impactReviews: ImpactReviewDto[];
  createdAt: string;
}

/** Experiments page: the current tab plus the number of experiments per status (tab counters and KPI cards). */
export interface ExperimentListDto extends Paginated<ExperimentDto> {
  counts: Record<ExperimentStatus | "all", number>;
}

/** Decisions journal: the filtered page plus the breakdown over the period and platform (KPI cards). */
export interface DecisionListDto extends Paginated<DecisionDto> {
  counts: { total: number; success: number; failed: number; positive: number; negative: number };
}

export interface AutoRuleDto {
  id: string;
  name: string;
  level: AutoRuleLevel;
  metric: AutoRuleMetric;
  operator: AutoRuleOperator;
  threshold: number;
  windowDays: number;
  minSpendMinor: number;
  action: AutoRuleAction;
  enabled: boolean;
}

export interface GuardrailDto {
  configured: boolean;
  currency: string;
  maxDailySpendMinor: number | null;
  maxBudgetIncreasePct: number;
  minHoursBetweenBudgetChanges: number;
  maxSingleBudgetMinor: number | null;
  minRoas: number | null;
  maxCpaMinor: number | null;
  stopLoss: { enabled: boolean; targetCpaMultiple: number; pauseCpaMultiple: number; minSpendMinor: number };
  creativeFatigue: { enabled: boolean; maxFrequency: number; ctrDropPct: number; minImpressions: number };
  autoRules: AutoRuleDto[];
  requireApproval: { budgetIncrease: boolean; budgetDecrease: boolean; activate: boolean; pause: boolean; createCampaign: boolean };
  aiProposalsEnabled: boolean;
  killSwitch: boolean;
  allowedPlatforms: AdPlatform[];
  updatedAt: string | null;
  /** Context shown next to the caps: targets from the business profile and the active daily budgets. */
  targets: AdTargets;
  activeDailyBudgetMinor: number;
}

export interface MediaBuyerThreadSummaryDto {
  id: string;
  title: string;
  preview: string;
  messagesCount: number;
  lastMessageAt: string;
}

export interface MediaBuyerMessageDto {
  role: "user" | "assistant";
  content: string;
  toolsUsed: string[];
  createdAt: string;
}

export interface MediaBuyerThreadDto extends MediaBuyerThreadSummaryDto {
  messages: MediaBuyerMessageDto[];
}

export interface MediaBuyerReplyDto {
  thread: MediaBuyerThreadSummaryDto;
  message: MediaBuyerMessageDto;
  usage: { inputTokens: number; outputTokens: number; cachedTokens: number };
}

export interface BriefingDto {
  source: "ai" | "rules";
  reason: string | null;
  text: string;
  generatedAt: string;
  highlights: Array<{ label: string; value: string; delta: number | null; goodWhen: "up" | "down" }>;
  alerts: number;
  recommendations: number;
}

export interface MediaBuyerPlanDto {
  draftId: string;
  link: string;
  name: string;
  rationale: string[];
  unresolvedInterests: string[];
}

export interface IntelStatusDto {
  currency: string;
  targets: AdTargets;
  killSwitch: boolean;
  aiProposalsEnabled: boolean;
  accounts: number;
  lastSyncAt: string | null;
  counts: { recommendations: number; approved: number; alerts: number; critical: number };
  ai: { available: boolean; reason: string | null };
}

/** French labels of the read-only tools the AI media buyer may call (shown under its answers). */
export const MEDIA_BUYER_TOOL_LABELS: Record<string, string> = {
  campaign_performance: "Performance des campagnes",
  top_bottom_ads: "Meilleures et moins bonnes publicités",
  recommendations_list: "Recommandations",
  budget_pacing: "Rythme du budget mensuel",
  audience_ideas: "Idées d'audiences",
  cod_true_metrics: "Rentabilité réelle (COD)",
  alerts_list: "Alertes publicité",
};
